<script setup>
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DocumentForm from './DocumentForm.vue';

defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close', 'createSuccess']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const i18nKey = 'CAPTAIN.DOCUMENTS.CREATE';

const getErrorMessage = error => {
  const apiError = parseAPIErrorResponse(error);
  if (typeof apiError === 'string') return apiError;
  return error?.message || t(`${i18nKey}.ERROR_MESSAGE`);
};

const handleSubmit = async newDocuments => {
  const documents = Array.isArray(newDocuments) ? newDocuments : [newDocuments];
  let createdCount = 0;

  try {
    for (const document of documents) {
      await store.dispatch('captainDocuments/create', document);
      createdCount += 1;
    }

    emit('createSuccess');
    const successKey =
      createdCount > 1
        ? `${i18nKey}.SUCCESS_MESSAGE_MANY`
        : `${i18nKey}.SUCCESS_MESSAGE`;
    useAlert(t(successKey, { count: createdCount }));
    dialogRef.value.close();
  } catch (error) {
    if (createdCount > 0) {
      emit('createSuccess');
      useAlert(
        t(`${i18nKey}.PARTIAL_SUCCESS_MESSAGE`, { count: createdCount })
      );
      dialogRef.value.close();
      return;
    }

    useAlert(getErrorMessage(error));
  }
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t(`${i18nKey}.TITLE`)"
    :description="$t('CAPTAIN.DOCUMENTS.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <DocumentForm
      :assistant-id="assistantId"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
</template>
